/**
 * @file いま開いている文脈 (資料・ページ) に合うノートの並べ方。
 *
 * @what: ノートを「ページ一致 → 資料一致 → その他」の順に並べる。同じ順位の中は
 *   受け取った並び (API の最近さわった順) を保つ。文脈の資料 id は別冊 (companion) を
 *   両方向にたどって集める。
 *   まなぶ人での絞り込み (notesForLearner) もここに置く。
 * @why: 問題編で書いたメモが解答編を開いたときにも出るように、companion 関係は利用者が
 *   紐づけ直さなくても自動で追随する (代表 2026-08-29)。
 *   メモはまなぶ人のもので、一覧は家族の全件が返るので client で絞る (復習カードと同じ)。
 *   学習者を持たない古いメモはどのまなぶ人でも残す。※なぜなら割り当て前に一覧から落ちると、そのメモがあること自体に
 *   気づけないためである。
 * @failure: 純関数。未知の kind の紐づけは「一致しない」として扱う (落とさない)。
 */

import type { StudyNote } from "../api/notes";
import type { StudySource } from "../api/sources";

/**
 * 文脈の資料 id の集合 (自分 + 自分の別冊 + 自分が別冊なら本体とその別冊)。
 */
export function contextSourceIds(sources: readonly StudySource[], sourceId: string): Set<string> {
  const ids = new Set<string>([sourceId]);
  const self = sources.find((s) => s.id === sourceId);
  if (self?.companion_of) ids.add(self.companion_of);
  for (const s of sources) {
    if (s.companion_of && ids.has(s.companion_of)) ids.add(s.id);
  }
  return ids;
}

/** 並べ替えの順位。0 = ページ一致 / 1 = 資料一致 / 2 = その他。 */
export function noteContextRank(
  note: StudyNote,
  sourceIds: ReadonlySet<string>,
  page?: number,
): 0 | 1 | 2 {
  let rank: 0 | 1 | 2 = 2;
  for (const ref of note.refs ?? []) {
    if (ref.kind !== "source" || !sourceIds.has(ref.source_id)) continue;
    if (page !== undefined && ref.page === page) return 0;
    rank = 1;
  }
  return rank;
}

export function sortNotesForContext(
  notes: readonly StudyNote[],
  sourceIds: ReadonlySet<string>,
  page?: number,
): StudyNote[] {
  return notes
    .map((note, i) => ({ note, i, rank: noteContextRank(note, sourceIds, page) }))
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map((x) => x.note);
}

/** まなぶ人のメモに絞る (学習者を持たない古いメモは残す)。learnerId が null なら全件を返す。 */
export function notesForLearner(
  notes: readonly StudyNote[],
  learnerId: string | null,
): StudyNote[] {
  if (learnerId === null) return [...notes];
  return notes.filter((n) => !n.learner_person_id || n.learner_person_id === learnerId);
}
